import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { X, Percent } from "lucide-react";

/**
 * Gerçekleşme % Modalı — %10 ile %100 arası, 10'ar artan seçim.
 * Kullanım: <RealizationModal open onClose={fn} onSaved={fn} />
 * Backend: GET/PUT /api/settings { realization_pct }
 */
export default function RealizationModal({ open, onClose, onSaved, scope = "shop" }) {
  // scope: "shop" → realization_shop_pct, "diger" → realization_diger_pct
  const fieldName = scope === "diger" ? "realization_diger_pct" : "realization_shop_pct";
  const label = scope === "diger" ? "Tekne Diğer Maliyet" : "Alışveriş";
  const [pct, setPct] = useState(100);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/settings");
        const raw = data?.[fieldName];
        const parsed = (raw === undefined || raw === null || raw === "") ? 100 : parseInt(raw, 10);
        const cur = Math.max(0, Math.min(100, Number.isFinite(parsed) ? parsed : 100));
        setPct(cur);
      } catch (e) {
        console.error("Realization load:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, fieldName]);

  if (!open) return null;

  const options = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { [fieldName]: pct });
      onSaved?.(pct);
      onClose?.();
    } catch (e) {
      console.error("Realization save:", e);
      alert("Kaydedilemedi, tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="realization-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        className="tk-card p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-lg font-display font-semibold flex items-center gap-2">
              <Percent className="w-5 h-5" style={{ color: "hsl(var(--primary))" }} />
              Tahmini {label} giderleri tamamen gerçek gider oldu mu?
            </div>
            <div className="text-xs mt-1" style={{ color: "hsl(var(--text-2))" }}>
              %100 = tahminler tamamen gerçek oldu (sadece fiili ödemeler sayılır). Daha düşük %&apos;de tahmini kısım orantılı olarak eklenir.
            </div>
          </div>
          <button
            data-testid="realization-modal-close"
            onClick={onClose}
            className="tk-btn-ghost !p-1.5"
            title="Kapat"
          ><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm" style={{ color: "hsl(var(--text-2))" }}>Yükleniyor…</div>
        ) : (
          <>
            <div className="grid grid-cols-6 sm:grid-cols-11 gap-2 my-4">
              {options.map((v) => {
                const active = pct === v;
                return (
                  <button
                    key={v}
                    type="button"
                    data-testid={`realization-opt-${v}`}
                    onClick={() => setPct(v)}
                    className={`py-2.5 rounded-lg text-sm font-semibold transition border ${active ? "" : "hover:bg-[hsl(var(--surface-2))]"}`}
                    style={{
                      backgroundColor: active ? "hsl(var(--primary))" : "hsl(var(--surface))",
                      color: active ? "white" : "hsl(var(--text-1))",
                      borderColor: active ? "hsl(var(--primary))" : "hsl(var(--border))",
                    }}
                  >%{v}</button>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                data-testid="realization-cancel-btn"
                onClick={onClose}
                className="tk-btn-ghost"
              >Vazgeç</button>
              <button
                data-testid="realization-save-btn"
                onClick={save}
                disabled={saving}
                className="tk-btn disabled:opacity-60"
              >{saving ? "Kaydediliyor…" : `%${pct} olarak kaydet`}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
